/**
 * Maps a running flow's reasoning-log stream onto the nodes drawn on the editor
 * canvas.
 *
 * Two problems this solves:
 *
 *  1. A sub-flow is a *single* node on the parent canvas (it carries
 *     `linked_flow_template_code`), but its inner activities log their own
 *     `activityTemplateCode`, which matches nothing on that canvas. Without a
 *     roll-up, the one node actually executing would be the only one that never
 *     lights up. `buildActivityNodeMap` therefore resolves every descendant
 *     activity code to the enclosing sub-flow node.
 *
 *  2. Fan-out runs several instances of a sub-graph in parallel, distinguished
 *     only by `branchIdentifier`. `deriveRunState` keeps one lane per branch so
 *     concurrent branches can never mark each other complete — the failure mode
 *     a single global "current activity" pointer would have.
 */

/** Lane key used for entries that carry no branchIdentifier. */
export const MAIN_BRANCH = '__main__';

/**
 * Name keys are namespaced so they can share one Map with the code keys without
 * any chance of collision.
 */
const nameKey = (name) => `name:${String(name ?? '').trim().toLowerCase()}`;

/**
 * Resolves a reasoning entry to a canvas node id.
 *
 * Code first, then name. The name fallback matters: a flow template's node codes
 * are not always the codes the runtime reports, but the display names line up
 * exactly, so matching on code alone can silently resolve nothing and leave the
 * whole canvas unhighlighted.
 */
export const resolveEntryNode = (activityNodeMap, entry) => {
  if (!activityNodeMap || !entry) return undefined;
  return (
    activityNodeMap.get(entry.activityTemplateCode) ??
    activityNodeMap.get(nameKey(entry.activityTemplateName))
  );
};

/**
 * The single place reasoning-log field names appear. Both camelCase and
 * snake_case are accepted so a backend naming difference doesn't blank the
 * canvas; correcting a name here fixes every consumer.
 */
export const normalizeReasoningEntry = (raw) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    return {
      reasoning: raw,
      activityTemplateName: '',
      activityTemplateCode: '',
      branchIdentifier: '',
      progressPercent: undefined,
      datetime: null,
    };
  }
  if (typeof raw !== 'object') return null;
  return {
    reasoning: raw.reasoning ?? raw.message ?? '',
    activityTemplateName: raw.activityTemplateName ?? raw.activity_template_name ?? '',
    activityTemplateCode: raw.activityTemplateCode ?? raw.activity_template_code ?? '',
    branchIdentifier: raw.branchIdentifier ?? raw.branch_identifier ?? '',
    progressPercent: raw.progressPercent ?? raw.progress_percent,
    datetime: raw.datetime ? new Date(raw.datetime) : null,
  };
};

/**
 * Normalises the several shapes the reasoning websocket emits into a flat list.
 * `mode: 'replace'` means the payload is the full log so far; `'append'` means
 * it extends what we already hold.
 */
export const extractReasoningEntries = (data) => {
  const status = String(data?.fullDocument?.status ?? data?.status ?? '').toLowerCase();
  const pack = (list, mode) => ({
    entries: (Array.isArray(list) ? list : []).map(normalizeReasoningEntry).filter(Boolean),
    mode,
    status,
  });

  if (Array.isArray(data)) return pack(data, 'replace');
  if (data?.type === 'delta') return pack(data.appendedReasonings, 'append');
  if (data?.type === 'error') {
    return pack([{ reasoning: `Error: ${data.error || 'Unknown error'}` }], 'append');
  }
  if (Array.isArray(data?.reasonings)) return pack(data.reasonings, 'replace');
  if (Array.isArray(data?.fullDocument?.reasonings)) return pack(data.fullDocument.reasonings, 'replace');
  if (Array.isArray(data?.appendedReasonings)) return pack(data.appendedReasonings, 'append');
  return pack([data], 'append');
};

/** Activity codes declared by a flow template's own configuration nodes. */
const templateNodes = (flowTemplate) => flowTemplate?.configuration?.nodes ?? [];

/**
 * Walks a linked flow template and returns every activity code it contains,
 * including those of nested sub-flows. `visited` guards against cyclic or
 * self-referencing links; a fetch failure yields an empty list so the map
 * degrades to partial rather than failing the run.
 */
const collectDescendants = async (flowTemplateCode, fetchFlowTemplate, visited) => {
  if (!flowTemplateCode || visited.has(flowTemplateCode)) return [];
  visited.add(flowTemplateCode);

  let flowTemplate;
  try {
    flowTemplate = await fetchFlowTemplate(flowTemplateCode);
  } catch {
    return [];
  }

  const found = [];
  for (const node of templateNodes(flowTemplate)) {
    found.push({
      code: node?.data?.activity_template_code || node?.data?.activityTemplateCode,
      name: node?.data?.activity_template_name || node?.data?.activityTemplateName,
    });
    const nested = node?.data?.linked_flow_template_code;
    if (nested) {
      found.push(...(await collectDescendants(nested, fetchFlowTemplate, visited)));
    }
  }
  return found;
};

/**
 * Builds the lookup used by `resolveEntryNode`: activity code -> canvas node id,
 * plus a namespaced `name:<lowercased name>` -> node id entry for each activity.
 *
 * Canvas nodes are mapped first so a node's own identity always wins over the
 * same code or name appearing inside some sub-flow. Descendants then fill in,
 * each resolving to the sub-flow node that encloses it.
 *
 * @param {Array}    nodes             ReactFlow nodes currently on the canvas
 * @param {Function} fetchFlowTemplate async (code) => flowTemplate
 */
export const buildActivityNodeMap = async (nodes, fetchFlowTemplate) => {
  const map = new Map();
  const list = Array.isArray(nodes) ? nodes : [];

  const add = ({ code, name }, nodeId) => {
    if (code && !map.has(code)) map.set(code, nodeId);
    const key = name ? nameKey(name) : null;
    if (key && !map.has(key)) map.set(key, nodeId);
  };

  for (const node of list) {
    add(
      {
        code: node?.data?.activity_template_code || node?.data?.activityTemplateCode,
        name: node?.data?.activity_template_name || node?.data?.activityTemplateName,
      },
      node.id
    );
  }

  for (const node of list) {
    const linked = node?.data?.linked_flow_template_code;
    if (!linked) continue;
    const descendants = await collectDescendants(linked, fetchFlowTemplate, new Set());
    for (const descendant of descendants) add(descendant, node.id);
  }

  return map;
};

/**
 * Reduces the *whole* entry list into per-node run state.
 *
 * Deriving from the full array rather than mutating state incrementally is what
 * makes drill-down work: when the canvas changes the map changes, and the same
 * entries re-derive against the new canvas with no replay bookkeeping and no
 * stale node state left behind.
 *
 * Completion is inferred per lane — when a new activity code appears in a lane,
 * that lane's previous activity is finished. Entries whose code isn't in the map
 * (e.g. an activity that isn't represented on any canvas) are simply skipped;
 * they still render in the run panel.
 *
 * @returns {{ nodeStates: Map<string, object>, branches: string[] }}
 */
export const deriveRunState = (entries, activityNodeMap, options = {}) => {
  const { runStatus, selectedBranch } = options;
  const list = Array.isArray(entries) ? entries : [];

  // Branches are collected across every entry, so the branch filter itself never
  // changes which branches are on offer.
  const branches = [];
  for (const entry of list) {
    const key = entry?.branchIdentifier || MAIN_BRANCH;
    if (!branches.includes(key)) branches.push(key);
  }

  const nodeStates = new Map();
  if (!activityNodeMap || activityNodeMap.size === 0) return { nodeStates, branches };

  const lanes = new Map();            // branch key -> node id currently running
  const perNode = new Map();          // node id  -> Map(branch key -> status)

  const setState = (nodeId, branchKey, status) => {
    let byBranch = perNode.get(nodeId);
    if (!byBranch) {
      byBranch = new Map();
      perNode.set(nodeId, byBranch);
    }
    byBranch.set(branchKey, status);
  };

  const seenBranches = new Set();

  // Completing a lane head that will never receive another entry. Without this,
  // the activity a fan-out fans *out of* (and each branch head after fan-in)
  // would pulse for the rest of the run.
  const closeLane = (laneKey, exceptNodeId) => {
    const head = lanes.get(laneKey);
    if (head && head !== exceptNodeId) {
      setState(head, laneKey, 'done');
      lanes.delete(laneKey);
    }
  };

  for (const entry of list) {
    const branchKey = entry?.branchIdentifier || MAIN_BRANCH;
    if (selectedBranch && selectedBranch !== branchKey) continue;

    const nodeId = resolveEntryNode(activityNodeMap, entry);
    if (!nodeId) continue;

    if (branchKey === MAIN_BRANCH) {
      // Fan-in: the main lane resuming means the branches are finished.
      for (const key of [...lanes.keys()]) {
        if (key !== MAIN_BRANCH) closeLane(key, nodeId);
      }
    } else if (!seenBranches.has(branchKey)) {
      // Fan-out: a new branch lane means the main lane's activity fanned out.
      seenBranches.add(branchKey);
      closeLane(MAIN_BRANCH, nodeId);
    }

    const previous = lanes.get(branchKey);
    // Consecutive activities inside one sub-flow resolve to the same node, so
    // this leaves the sub-flow node steadily running instead of flickering.
    if (previous && previous !== nodeId) setState(previous, branchKey, 'done');
    setState(nodeId, branchKey, 'running');
    lanes.set(branchKey, nodeId);
  }

  if (runStatus === 'success') {
    for (const [branchKey, nodeId] of lanes) setState(nodeId, branchKey, 'done');
  } else if (runStatus === 'failure') {
    for (const [branchKey, nodeId] of lanes) setState(nodeId, branchKey, 'failed');
  }

  for (const [nodeId, byBranch] of perNode) {
    const values = [...byBranch.values()];
    const running = values.filter((v) => v === 'running').length;
    const failed = values.filter((v) => v === 'failed').length;
    const done = values.filter((v) => v === 'done').length;
    nodeStates.set(nodeId, {
      status: running > 0 ? 'running' : failed > 0 ? 'failed' : 'done',
      running,
      failed,
      done,
      // Branches observed *so far* — fan-out width is data-dependent and unknown
      // until each branch first emits, so this denominator grows during a run.
      observedBranches: values.length,
    });
  }

  return { nodeStates, branches };
};

/** Human label for a branch lane, e.g. "Main" / "Branch 2". */
export const branchLabel = (branchKey, index) =>
  branchKey === MAIN_BRANCH ? 'Main' : `Branch ${index + 1}`;
